<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'

import CompanySvg from '@/assets/svg/home/page2/page1-company.svg'
import AiPlaySvg from '@/assets/svg/home/page2/page2-al-play.svg'
import DeepseekSvg from '@/assets/svg/home/page2/page2-deepseek.svg'
import LiveSvg from '@/assets/svg/home/page2/page2-live.svg'
import OemSvg from '@/assets/svg/home/page2/page2-OEM.svg'
import TeacherSvg from '@/assets/svg/home/page2/page2-teacher.svg'
import type {  CardItem } from '@/config/constantsType'
import { isAllEmpty } from '@pureadmin/utils'
import { checkAndNavigate } from '@/utils/redirect'
const { t } = useI18n({ useScope: 'global' })
const isVisible = ref(false)
const page2Ref = ref<HTMLElement | null>(null)
const highlightedCard = ref(1)

const cardList: (CardItem)[] = [
  { id: 1, key: 'training', icon: AiPlaySvg, link: '' },
  { id: 2, key: 'oem', icon: OemSvg, link: '/workspace/welcome' },
  { id: 3, key: 'dev', icon: CompanySvg, link: '' },
  { id: 4, key: 'teacher', icon: TeacherSvg, link: '' },
  { id: 5, key: 'live', icon: LiveSvg, link: '' },
  { id: 6, key: 'commerce', icon: DeepseekSvg, link: '' }
]
const handleCardClick = (link:string) => {
  if (isAllEmpty(link)) {
    return;
  }
  checkAndNavigate(link);
}

onMounted(() => {
  const io = new IntersectionObserver(
    ([e]) => {
      isVisible.value = e.isIntersecting
      highlightedCard.value = e.isIntersecting ? 1 : 0
    },
    { threshold: 0.2 }
  )
  page2Ref.value && io.observe(page2Ref.value)
})
</script>

<template>
  <div ref="page2Ref" class="flex flex-col justify-center items-center">
    <!-- 标题 -->
    <div class="flex flex-col mt-[6.56rem] mb-[4.5rem] justify-center items-center overflow-hidden">
      <div class="text-[3rem] font-[600] transition-all duration-700"
        :class="isVisible ? 'translate-x-0 opacity-100' : 'translate-x-[-100px] opacity-0'">
        {{ t('page2.slogan1') }}
      </div>
      <div class="text-[3rem] font-[600] text-main-primary transition-all duration-700"
        :class="isVisible ? 'translate-x-0 opacity-100' : 'translate-x-[100px] opacity-0'"
        style="transition-delay: 200ms">
        {{ t('page2.slogan2') }}
      </div>
    </div>
    <!-- 内容 -->
    <div>
      <div class="grid grid-cols-3 gap-[0.4rem]">
        <div v-for="(card, index) in cardList" :key="card.id"
          class="flex border-2 hover:border-main-primary/10 hover-shadow border-[#fff] rounded-md flex-col justify-center
                 items-center bg-main-primary/8 w-[28rem] h-[19.6rem] cursor-pointer transition-all duration-50 relative" :class="[
                  isVisible ? 'translate-y-0 opacity-100' : 'translate-y-[50px] opacity-0',
                  'hover:translate-y-[-0.5rem] hover:border-main-primary'
                ]" :style="{ transitionDelay: `${200 + index * 100}ms` }" @click="handleCardClick(card.link)">
          <component :is="card.icon" class="w-[4.3rem] h-[4.3rem] transition-transform duration-200"
            :class="isVisible ? 'scale-100' : 'scale-0'" :style="{ transitionDelay: `${600 + index * 100}ms` }" />
          <!-- 实时翻译 -->
          <div class="text-[#3D3D3D] font-[600] my-[0.8rem]">
            {{ t(`page2.cards.${card.key}.title`) }}
          </div>
          <div class="text-[0.9rem] text-[#797676]">
            {{ t(`page2.cards.${card.key}.d1`) }}
          </div>
          <div class="text-[0.9rem] text-[#797676]">
            {{ t(`page2.cards.${card.key}.d2`) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<!-- 样式完全保留 -->
<style scoped>
.hover-shadow {
  transition: all 0.2s ease-out;
}

.hover-shadow:hover {
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}

.translate-x-0 {
  transform: translateX(0)
}

.translate-x-\[-100px\] {
  transform: translateX(-100px)
}

.translate-x-\[100px\] {
  transform: translateX(100px)
}

.translate-y-0 {
  transform: translateY(0)
}

.translate-y-\[50px\] {
  transform: translateY(50px)
}

.scale-0 {
  transform: scale(0)
}

.scale-100 {
  transform: scale(1)
}

.opacity-0 {
  opacity: 0
}

.opacity-100 {
  opacity: 1
}
</style>